import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../context/API';
import { Log, LogsApiResponse, Device, DevicesApiResponse, Merchant, MerchantsApiResponse, LogsRequestParams } from '../types';

interface ActionLogsState {
  logs: Log[];
  devices: Device[];
  merchants: Merchant[];
  totalPages: number;
  totalItems: number;
  loading: boolean;
  error: string | null;
}

const initialState: ActionLogsState = {
  logs: [],
  devices: [],
  merchants: [],
  totalPages: 1,
  totalItems: 0,
  loading: false,
  error: null,
};

// Fetch logs
export const fetchLogs = createAsyncThunk<
  { logs: Log[]; totalPages: number; totalItems: number },
  LogsRequestParams,
  { rejectValue: string }
>('actionLogs/fetchLogs', async (params, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await API.get<LogsApiResponse>('/merchant/logs', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params,
    });

    if (response.data && response.data.success && response.data.data) {
      const logs = response.data.data.data || [];
      const meta = response.data.data.meta;
      return {
        logs,
        totalPages: meta?.totalPages || 1,
        totalItems: meta?.totalItems || 0,
      };
    } else {
      return rejectWithValue('No logs data received from server');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch logs'
    );
  }
});

// Fetch devices
export const fetchDevices = createAsyncThunk<
  Device[],
  { merchantId?: string },
  { rejectValue: string }
>('actionLogs/fetchDevices', async ({ merchantId }, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    const params: any = {};
    if (merchantId) {
      params.merchantId = merchantId;
    }

    const response = await API.get<DevicesApiResponse>('/merchant/devices', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params,
    });

    if (response.data && response.data.success && response.data.data) {
      return response.data.data;
    } else {
      return rejectWithValue('No devices data received from server');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch devices'
    );
  }
});

// Fetch merchants (Admin Only)
export const fetchMerchants = createAsyncThunk<
  Merchant[],
  void,
  { rejectValue: string }
>('actionLogs/fetchMerchants', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await API.get<MerchantsApiResponse>('/merchant', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data && response.data.success && response.data.data) {
      return response.data.data;
    } else {
      return rejectWithValue('No merchants data received from server');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch merchants'
    );
  }
});

const actionLogsSlice = createSlice({
  name: 'actionLogs',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearLogs: (state) => {
      state.logs = [];
      state.totalPages = 1;
      state.totalItems = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch logs
      .addCase(fetchLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload.logs;
        state.totalPages = action.payload.totalPages;
        state.totalItems = action.payload.totalItems;
        state.error = null;
      })
      .addCase(fetchLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch logs';
        state.logs = [];
        state.totalPages = 1;
        state.totalItems = 0;
      })
      // Fetch devices
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.devices = action.payload;
      })
      .addCase(fetchDevices.rejected, (state) => {
        state.devices = [];
      })
      // Fetch merchants
      .addCase(fetchMerchants.fulfilled, (state, action) => {
        state.merchants = action.payload;
      })
      .addCase(fetchMerchants.rejected, (state) => {
        state.merchants = [];
      });
  },
});

export const { clearError, clearLogs } = actionLogsSlice.actions;
export default actionLogsSlice.reducer;
